// @flow
import { useState } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { AiBadge, NoData, Portal, useQaByClientQuery } from '../../../shared';
import { Preloader } from '@coverright/features';
import { ClientSelector } from '../../../features';
import { getUserName } from '@coverright/utils';
import moment from 'moment';
import { QaScoreRing, ScoreText } from '../ui/qa-shared';
import { useCallDrawer } from '../ui/call-drawer-by-aircall';

export const ByClient = () => {
  const [client, setClient] = useState<any>(null);
  const { open } = useCallDrawer();

  const { data, loading } = useQaByClientQuery({
    variables: { clientId: client?.clientId },
    skip: !client?.clientId,
    fetchPolicy: 'cache-and-network',
  });

  const report = data?.qaByClient;
  const agg = report?.aggregate;

  return (
    <Stack gap={3}>
      <Stack direction={'row'} alignItems={'center'} gap={2} justifyContent={'space-between'} flexWrap={'wrap'}>
        {client ? (
          <Stack direction={'row'} alignItems={'center'} gap={1.5}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: '#E5C76B' }}>
              {(getUserName(client) || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('')}
            </Avatar>
            <Box>
              <Typography variant={'h5'}>{getUserName(client)}</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                {client.email} {client.state ? `Â· ${client.state}` : ''}
                {report ? ` Â· ${report.calls.length} scored calls` : ''}
              </Typography>
            </Box>
          </Stack>
        ) : <Typography variant={'subtitle1'} color={'text.secondary'}>Pick a client to drill in</Typography>}
        <Box minWidth={260}>
          <ClientSelector value={client || undefined} onChange={setClient} />
        </Box>
      </Stack>

      {client && loading && !data && <Preloader sx={{ height: 250 }} />}

      {agg && (
        <Stack direction={'row'} gap={3} flexWrap={'wrap'} alignItems={'flex-start'}>
          <Portal p={3} sx={{ alignItems: 'center', minWidth: 220 }}>
            <Stack alignItems={'center'} gap={1.5}>
              <QaScoreRing score={agg.summary.averageScore} label={'Avg across calls'} />
              <AiBadge label={'AI Scored'} />
            </Stack>
          </Portal>

          <Stack flex={1} gap={3} minWidth={340}>
            <Portal p={2}>
              <Typography variant={'subtitle1'} mb={2}>Most failed questions (this client)</Typography>
              {agg.mostFailedQuestions.length ? agg.mostFailedQuestions.map(q => (
                <Stack key={q.questionId} direction={'row'} alignItems={'center'} sx={{ py: 0.75 }}>
                  <Typography variant={'body2'} flex={1}>
                    <Box component={'span'} sx={{ color: '#9CA3AF', fontWeight: 600, mr: 0.75 }}>{q.questionId}</Box>
                    {q.question}
                  </Typography>
                  <Typography variant={'body2'} sx={{ color: '#B42318', fontWeight: 600 }}>{q.failCount} calls</Typography>
                </Stack>
              )) : <NoData height={80}>No failed questions</NoData>}
            </Portal>

            <Portal p={2}>
              <Typography variant={'subtitle1'} mb={2}>Calls</Typography>
              <Stack direction={'row'} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
                <Typography variant={'caption'} color={'text.secondary'} flex={1}>Date</Typography>
                <Typography variant={'caption'} color={'text.secondary'} width={180}>Agent</Typography>
                <Typography variant={'caption'} color={'text.secondary'} width={140}>Disposition</Typography>
                <Typography variant={'caption'} color={'text.secondary'} width={70} textAlign={'right'}>Score</Typography>
              </Stack>
              {report!.calls.map(c => (
                <Stack key={c.airCallId} direction={'row'} alignItems={'center'} sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f6fafb' } }}
                       onClick={() => open(c.airCallId)}>
                  <Typography variant={'body2'} flex={1}>{c.callDate ? moment(c.callDate).format('L LT') : '-'}</Typography>
                  <Typography variant={'body2'} width={180}>{c.agentName || '-'}</Typography>
                  <Typography variant={'body2'} width={140} sx={{ textTransform: 'capitalize' }}>{c.dispositionClass}</Typography>
                  <Box width={70} textAlign={'right'}><ScoreText score={c.effectiveScore} variant={'body2'} /></Box>
                </Stack>
              ))}
            </Portal>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};